import React, { useEffect, useRef, useState } from "react";

import "../../styles/aisdr.css";
import {
  IconBook,
  IconChat,
  IconChatOutline,
  IconMinimize,
  IconSend,
} from "./icons";
import { startVisit, streamChat } from "../../functions/aisdrChat";

const MAX_INPUT_LENGTH = 1000;

let messageSeq = 0;
const nextId = () => {
  messageSeq += 1;
  return `m${messageSeq}`;
};

const AiSdrChat = (props) => {
  const {
    config,
    account,
    online,
    domain,
    visitorId,
    initialMessage,
    onInitialMessageSent,
    onMinimize,
    onStartMeeting,
  } = props;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [view, setView] = useState("chat");
  const [visit, setVisit] = useState({ conversationId: null, visitCount: null });
  const [initialized, setInitialized] = useState(false);
  // クイック返信は「訪問者がまだ一度も自分で発言していない間」は出し続ける。
  // オンライン（カレンダーに空きがありアポなし商談を受け付けられる状態）でも、
  // CTA バブルが積まれて会話数が増えるだけで消えないようにする。
  const showQuickReplies = !messages.some((m) => m.role === "user");

  const msgsRef = useRef(null);
  const initialSentRef = useRef(false);

  const subButtons = (account?.button ?? []).filter(
    (item) => item.title !== "" && item.url !== ""
  );

  // 訪問者を登録し、挨拶文と過去の会話履歴を受け取る。
  // API に繋がらない場合は設定の挨拶文だけで会話を始める（無言で壊れない）。
  useEffect(() => {
    let cancelled = false;

    const buildInitial = (greeting, history) => {
      const initial = (history ?? [])
        .filter((item) => item.text)
        .map((item) => ({
          id: nextId(),
          role: item.role === "user" ? "user" : "assistant",
          type: "text",
          text: item.text,
        }));
      if (greeting) {
        initial.push({
          id: nextId(),
          role: "assistant",
          type: "text",
          text: greeting,
        });
      }
      if (online) {
        initial.push({
          id: nextId(),
          role: "assistant",
          type: "cta",
          text: "今すぐ商談が可能です。",
        });
      }
      return initial;
    };

    const init = async () => {
      const result = await startVisit({
        domain,
        visitor_id: visitorId,
        parent_page_url: document.referrer,
      });
      if (cancelled) return;

      if (result) {
        setVisit({
          conversationId: result.conversation_id,
          visitCount: result.visit_count,
        });
        setMessages(buildInitial(result.greeting, result.history));
      } else {
        setMessages(buildInitial(config.greetingText, []));
      }
      setInitialized(true);
    };

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [domain, visitorId, online]);

  const scrollToBottom = () => {
    const el = msgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  useEffect(scrollToBottom, [messages]);

  const appendMessage = (message) => {
    setMessages((prev) => [...prev, { id: nextId(), ...message }]);
  };

  const send = async (rawText) => {
    const message = (rawText ?? "").trim();
    if (message === "" || sending) return;

    setInput("");
    setSending(true);
    appendMessage({ role: "user", type: "text", text: message });

    // ストリーム描画用の空バブルを先に置く
    const replyId = nextId();
    setMessages((prev) => [
      ...prev,
      { id: replyId, role: "assistant", type: "text", text: "", pending: true },
    ]);

    const updateReply = (updater) => {
      setMessages((prev) =>
        prev.map((item) => (item.id === replyId ? updater(item) : item))
      );
    };

    try {
      let accumulated = "";
      await streamChat(
        {
          domain,
          visitor_id: visitorId,
          message,
          parent_page_url: document.referrer,
          visit_count: visit.visitCount,
          is_online: online,
        },
        (eventName, data) => {
          if (data && data.type === "token") {
            accumulated += data.delta;
            updateReply((item) => ({
              ...item,
              text: accumulated,
              pending: false,
            }));
          } else if (eventName === "done") {
            updateReply((item) => ({
              ...item,
              text: accumulated || item.text,
              pending: false,
            }));
            if (data && data.suggestions?.length) {
              appendMessage({
                role: "assistant",
                type: "suggestions",
                items: data.suggestions,
              });
            }
          } else if (data && data.type === "cta") {
            updateReply((item) => ({ ...item, pending: false }));
            appendMessage({ role: "assistant", type: "cta", text: data.text });
          }
        }
      );
      updateReply((item) => ({
        ...item,
        text: item.text || "…",
        pending: false,
      }));
    } catch (error) {
      console.log("AI SDR チャット応答に失敗", error);
      updateReply((item) => ({
        ...item,
        text: "申し訳ありません、ただ今応答できませんでした。お手数ですが少し時間をおいてお試しください。",
        pending: false,
      }));
    }
    setSending(false);
  };

  // 全画面プレイヤーのCTAから開かれたときは、その文言を1通目として送る。
  // LD が elInput.value に入れて handleSend() を叩いていたのと同じ挙動。
  useEffect(() => {
    if (!initialMessage || initialSentRef.current || !initialized) return;
    initialSentRef.current = true;
    send(initialMessage);
    if (onInitialMessageSent) onInitialMessageSent();
    // eslint-disable-next-line
  }, [initialMessage, initialized]);

  const avatar = (
    <div className="aisdr-ava">
      {account?.avatar ? (
        <img src={account.avatar} alt="" />
      ) : (
        <IconChat />
      )}
    </div>
  );

  const renderMessage = (message) => {
    if (message.type === "suggestions") {
      return (
        <div className="aisdr-sugs" key={message.id}>
          {message.items.map((item) => (
            <button
              key={item}
              type="button"
              className="aisdr-chip"
              onClick={() => send(item)}
            >
              {item}
            </button>
          ))}
        </div>
      );
    }

    if (message.type === "cta") {
      return (
        <div className="aisdr-row ai" key={message.id}>
          {avatar}
          <div>
            {message.text && <div className="aisdr-msg">{message.text}</div>}
            <button
              type="button"
              className="aisdr-cta"
              onClick={onStartMeeting}
              disabled={!online}
              style={{ marginTop: message.text ? "8px" : 0 }}
            >
              {account?.mainButton || "アポなし商談"}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`aisdr-row ${message.role === "user" ? "me" : "ai"}`}
        key={message.id}
      >
        {message.role !== "user" && avatar}
        <div className="aisdr-msg">
          {message.pending ? (
            <span className="aisdr-typing">
              <i />
              <i />
              <i />
            </span>
          ) : (
            message.text
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="aisdr-root"
      style={{ "--aisdr-accent": config.themeColor }}
    >
      <div className="aisdr-panel">
        <div className="aisdr-hd">
          <div className="logo">
            {account?.avatar ? (
              <img src={account.avatar} alt="" />
            ) : (
              <span>{(config.brandName || "A").slice(0, 1)}</span>
            )}
          </div>
          <div className="ti">
            <b>{config.brandName}</b>
            <i>
              <span className={`dot ${online ? "on" : "off"}`} />
              {online ? "オンライン" : "オフライン"}
              {account?.company ? ` ・ ${account.company}` : ""}
            </i>
          </div>
          <button type="button" className="min" onClick={onMinimize}>
            <IconMinimize />
          </button>
        </div>

        {view === "chat" ? (
          <>
            <div className="aisdr-msgs" ref={msgsRef}>
              {messages.map(renderMessage)}
            </div>

            <div className="aisdr-ft">
              {config.quickReplies?.length > 0 && showQuickReplies && (
                <div className="aisdr-qr">
                  {config.quickReplies.map((label) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => send(label)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <div className="aisdr-inp">
                <input
                  type="text"
                  value={input}
                  maxLength={MAX_INPUT_LENGTH}
                  placeholder="AIに相談する"
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") send(input);
                  }}
                />
                <button
                  type="button"
                  className="send"
                  disabled={input.trim() === "" || sending}
                  onClick={() => send(input)}
                >
                  <IconSend />
                </button>
              </div>
              <div className="aisdr-pw">Powered by ノンアポ</div>
            </div>
          </>
        ) : (
          <div className="aisdr-docs">
            {subButtons.length === 0 ? (
              <div className="aisdr-docs-empty">
                ご案内できる資料がまだ登録されていません。
              </div>
            ) : (
              subButtons.map((item) => (
                <div className="aisdr-card" key={item.title}>
                  {account?.thumbnail && (
                    <div
                      className="thumb"
                      style={{ backgroundImage: `url('${account.thumbnail}')` }}
                    />
                  )}
                  <div className="bd">
                    <h4>{item.title}</h4>
                    <a href={item.url} target="_top" rel="noreferrer">
                      資料を見る
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {subButtons.length > 0 && (
          <div className="aisdr-nav">
            <button
              type="button"
              className={`aisdr-nav-btn ${view === "chat" ? "active" : ""}`}
              onClick={() => setView("chat")}
              aria-label="チャット"
            >
              <IconChatOutline />
            </button>
            <button
              type="button"
              className={`aisdr-nav-btn ${view === "docs" ? "active" : ""}`}
              onClick={() => setView("docs")}
              aria-label="資料"
            >
              <IconBook />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiSdrChat;
